import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Phone, ArrowRight, ImagePlus, X, Star, ShieldCheck, Check } from "lucide-react";
import Seo from "@/components/site/Seo";
import { api, formatApiError } from "@/lib/api";
import { trackCall, trackQuoteSubmit } from "@/lib/analytics";
import { CONTACT, SERVICE_OPTIONS, BUDGET_OPTIONS } from "@/data/content";
import { useReviews } from "@/hooks/usePublicContent";

// Dedicated Google Ads landing page — no navigation, one goal: the quote.
// Hidden from search engines (noindex) so it never competes with organic pages.

const EMPTY = { name: "", email: "", phone: "", service: SERVICE_OPTIONS[0], suburb: "", budget: "", message: "" };
const MAX_PHOTOS = 8;
const MAX_BYTES = 10 * 1024 * 1024;

const TRUST = [
  "Free, detailed written quotes",
  "Rectified & large-format specialists",
  "All tile types — ceramic to natural stone",
  "Servicing Perth's premier suburbs",
];

export default function QuoteLanding() {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { items: reviews } = useReviews();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type.startsWith("image/") && f.size <= MAX_BYTES);
    if (valid.length < files.length) toast.error("Some files were skipped (images under 10MB only).");
    setPhotos((prev) => [...prev, ...valid].slice(0, MAX_PHOTOS));
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p));
      await api.post("/enquiries", fd);
      trackQuoteSubmit("ad_landing_page");
      setForm(EMPTY);
      setPhotos([]);
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#0f172a]/20 focus:border-[#9c7a4d] text-[#0f172a] placeholder:text-[#94a3b8] py-3 text-lg font-light outline-none transition-colors duration-300";

  const topReview = reviews.find((r) => r.rating >= 4) || reviews[0];

  return (
    <>
      <Seo
        pageKey="home"
        overrideTitle="Get a Free Tiling Quote in Perth | Perth Tiling WA"
        overrideDescription="Perth's complete tiling specialists. Bathrooms, kitchens, outdoor, commercial and feature walls — European-standard craftsmanship. Get your free, detailed quote today."
        noindex
      />
      <div data-testid="quote-landing-page" className="bg-[#f8fafc] min-h-screen">
        {/* Minimal header — logo + phone only, no navigation */}
        <header className="bg-[#0b1120] py-4">
          <div className="mx-auto max-w-[1200px] px-6 flex items-center justify-between">
            <img src="/brand/logo.png" alt="Perth Tiling WA" className="h-10 md:h-12 w-auto object-contain" />
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              onClick={() => trackCall("ad_landing_header")}
              data-testid="quote-landing-call"
              className="inline-flex items-center gap-2 bg-[#9c7a4d] text-white hover:bg-white hover:text-[#0f172a] px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <Phone className="h-3.5 w-3.5" /> {CONTACT.phone}
            </a>
          </div>
        </header>

        {/* Hero + form above the fold */}
        <section className="py-14 md:py-20">
          <div className="mx-auto max-w-[1200px] px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <p className="overline !text-[#9c7a4d] mb-5">Perth's Complete Tiling Specialists</p>
              <h1 className="font-serif font-light text-[#0f172a] text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight">
                Get your free tiling quote.
              </h1>
              <p className="mt-6 text-[#475569] text-lg font-light leading-relaxed max-w-md">
                Bathrooms, kitchens, outdoor areas, feature walls and commercial fit-outs — every tile type, installed with European-standard craftsmanship. Tell us about your project and we'll respond promptly with honest advice and a detailed quote.
              </p>

              <ul className="mt-8 space-y-3">
                {TRUST.map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#9c7a4d] shrink-0 mt-0.5" />
                    <span className="text-[#1e293b] font-light">{t}</span>
                  </li>
                ))}
              </ul>

              {topReview && (
                <div data-testid="quote-landing-review" className="mt-10 border-l-2 border-[#9c7a4d] pl-6 max-w-md">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: topReview.rating || 5 }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-[#f59e0b] text-[#f59e0b]" />
                    ))}
                  </div>
                  <p className="text-[#475569] font-light italic leading-relaxed">"{topReview.text}"</p>
                  <p className="mt-3 text-sm text-[#94a3b8] uppercase tracking-[0.15em]">
                    {topReview.author}{topReview.suburb ? ` — ${topReview.suburb}` : ""}
                  </p>
                </div>
              )}

              <div className="mt-10 flex items-center gap-3 text-[#64748b]">
                <ShieldCheck className="h-5 w-5 text-[#9c7a4d]" />
                <span className="text-sm font-light">Fully insured · Free site visits across Perth metro</span>
              </div>
            </div>

            {/* Form card */}
            <div className="border border-[#0f172a]/10 bg-white p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(33,29,24,0.35)]">
              <form onSubmit={submit} data-testid="quote-landing-form" className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <p className="sm:col-span-2 overline !text-[#94a3b8]">Request your free quote</p>
                  <div>
                    <input data-testid="quote-landing-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputClass} />
                  </div>
                  <div>
                    <input data-testid="quote-landing-phone" required value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" className={inputClass} />
                  </div>
                  <div className="sm:col-span-2">
                    <input data-testid="quote-landing-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputClass} />
                  </div>
                  <div className="sm:col-span-2">
                    <select data-testid="quote-landing-service" value={form.service} onChange={set("service")} className={`${inputClass} appearance-none cursor-pointer`}>
                      {SERVICE_OPTIONS.map((s) => (
                        <option key={s} value={s} className="bg-white text-[#0f172a]">{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <input data-testid="quote-landing-suburb" value={form.suburb} onChange={set("suburb")} placeholder="Your suburb" className={inputClass} />
                  </div>
                  <div>
                    <select data-testid="quote-landing-budget" value={form.budget} onChange={set("budget")} className={`${inputClass} appearance-none cursor-pointer`}>
                      <option value="" className="bg-white text-[#0f172a]">Budget (optional)…</option>
                      {BUDGET_OPTIONS.map((b) => (
                        <option key={b} value={b} className="bg-white text-[#0f172a]">{b}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <textarea data-testid="quote-landing-message" required rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about the space, materials and timeline…" className={`${inputClass} resize-none`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      data-testid="quote-landing-photos-input"
                      className="flex items-center justify-center gap-3 border border-dashed border-[#0f172a]/25 hover:border-[#9c7a4d] text-[#64748b] hover:text-[#9c7a4d] py-4 cursor-pointer transition-colors"
                    >
                      <ImagePlus className="h-5 w-5" />
                      <span className="text-sm">Add photos (optional)</span>
                      <input type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" />
                    </label>
                    {photos.length > 0 && (
                      <div className="mt-3 grid grid-cols-4 gap-2">
                        {photos.map((p, i) => (
                          <div key={i} className="relative group aspect-square border border-[#0f172a]/10 overflow-hidden">
                            <img src={URL.createObjectURL(p)} alt={p.name} className="h-full w-full object-cover" />
                            <button
                              type="button"
                              data-testid={`quote-landing-photo-remove-${i}`}
                              onClick={() => removePhoto(i)}
                              className="absolute top-1 right-1 h-6 w-6 flex items-center justify-center bg-black/60 text-white hover:bg-black/80 transition-colors"
                              aria-label="Remove photo"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      data-testid="quote-landing-submit"
                      type="submit"
                      disabled={loading}
                      className="group w-full inline-flex items-center justify-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] disabled:opacity-60 px-10 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-300"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                      {loading ? "Sending" : "Get My Free Quote"}
                    </button>
                    <p className="mt-3 text-center text-xs text-[#94a3b8]">No obligation. We respond within one business day.</p>
                  </div>
                </form>
            </div>
          </div>
        </section>

        {/* Minimal footer */}
        <footer className="bg-[#0b1120] text-[#94a3b8] py-8">
          <div className="mx-auto max-w-[1200px] px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <p>© {new Date().getFullYear()} Perth Tiling WA. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCall("ad_landing_footer")} className="hover:text-[#c9a87c] transition-colors">{CONTACT.phone}</a>
              <a href={`mailto:${CONTACT.email}`} className="hover:text-[#c9a87c] transition-colors">{CONTACT.email}</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
